import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ThemaCode, ThemaCodeType } from "reedy-data/api";
import { SearchType, Tab } from "~/shared/types/thema";
import {
  ThemaCodesSearchControls,
  ThemaCodesTabs,
} from "~/components/thema-codes";
import { toast } from "~/utils/toast";
import { Button } from "~/components/button";
import { useSession } from "~/contexts/auth-context";
import { mapOrganisationLanguageToThema } from "~/utils/thema-language-mapping";
import { useAnalytics } from "~/contexts/analytics-context";
import { useCodesQueryParam } from "./use-codes-query-param";

interface ThemaCodesContentProps {
  kindToggle?: ReactNode;
}

export const ThemaCodesContent = ({ kindToggle }: ThemaCodesContentProps) => {
  const { t } = useTranslation(["themaCodes"]);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.SUBJECTS);
  const { gaEvent } = useAnalytics();
  const [showNotesInline, setShowNotesInline] = useState(false);
  const [notesManuallyDisabled, setNotesManuallyDisabled] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>(SearchType.QUICK);
  const { submittedQuery, setSubmittedQuery } = useCodesQueryParam("THEMA");
  const [localQuery, setLocalQuery] = useState(submittedQuery);
  const { organisation } = useSession();
  const themaLanguage = organisation.effectiveLanguage
    ? mapOrganisationLanguageToThema(organisation.effectiveLanguage)
    : "en";
  const hasSubmittedQuery = Boolean(submittedQuery);

  useEffect(() => {
    // syncs the controlled search input to the URL-backed submitted query when it changes externally (e.g. navigation); the input is also locally editable, so it cannot be derived
    setLocalQuery(submittedQuery);
  }, [submittedQuery]);

  // Auto-enable notes when search is executed (unless manually disabled)
  useEffect(() => {
    if (hasSubmittedQuery && !notesManuallyDisabled) {
      // auto-enables inline notes once a search has been submitted, unless the user manually disabled them; showNotesInline is also user-toggleable
      setShowNotesInline(true);
    }
  }, [hasSubmittedQuery, notesManuallyDisabled]);

  const handleSearchQueryChange = (newQuery: string) => {
    setLocalQuery(newQuery);
  };

  const handleQueryClear = useCallback(() => {
    setLocalQuery("");
    setSubmittedQuery("");
  }, [setSubmittedQuery]);

  const handleDrillDown = useCallback(() => {
    if (hasSubmittedQuery) {
      handleQueryClear();
    }
  }, [handleQueryClear, hasSubmittedQuery]);

  const handleScopeNotesChecked = () => {
    const value = !showNotesInline;
    setShowNotesInline(value);
    // Track if user manually disabled notes during an active search
    if (!value && hasSubmittedQuery) {
      setNotesManuallyDisabled(true);
    } else if (value) {
      // Reset manual disable flag when user re-enables notes
      setNotesManuallyDisabled(false);
    }
  };

  const handleTabChange = useCallback(
    (value: string) => {
      const fromTab = activeTab;
      const toTab = value as Tab;

      if (fromTab !== toTab) {
        gaEvent({
          type: "thema_tab_change",
          payload: {
            book: "thema_codes_page",
            from_tab: fromTab,
            to_tab: toTab,
          },
        });
      }

      setActiveTab(toTab);
    },
    [activeTab, gaEvent],
  );

  const handleCopy = useCallback(
    async (code: string) => {
      try {
        await navigator.clipboard.writeText(code);
        gaEvent({
          type: "copy_thema_code",
          payload: {
            code,
            code_type:
              activeTab === Tab.QUALIFIERS
                ? ThemaCodeType.QUALIFIER
                : ThemaCodeType.SUBJECT,
            query: submittedQuery,
          },
        });
        toast.success(t("themaCodes:copy.success"));
      } catch {
        toast.error(t("themaCodes:copy.failed"));
      }
    },
    [t, submittedQuery, activeTab, gaEvent],
  );

  const renderCopyAction = useCallback(
    (item: ThemaCode) => {
      const handleCopyClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleCopy(item.code);
      };

      return (
        <Button
          variant="ghost"
          size="small"
          onClick={handleCopyClick}
          className="size-8 p-0 hover:text-primary-800 opacity-0 group-hover:opacity-100 transition-opacity group-hover:duration-200 duration-0"
          aria-label="copy"
          icon="copy"
        />
      );
    },
    [handleCopy],
  );

  return (
    <>
      <ThemaCodesSearchControls
        query={localQuery}
        showNotesInline={showNotesInline}
        searchType={searchType}
        onQueryChange={handleSearchQueryChange}
        onQuerySubmit={setSubmittedQuery}
        onSearchTypeChange={setSearchType}
        onToggleNotesInline={handleScopeNotesChecked}
        trailing={kindToggle}
      />
      <ThemaCodesTabs
        query={submittedQuery}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        themaLanguage={themaLanguage}
        showNotesInline={showNotesInline}
        renderRowAction={renderCopyAction}
        onDrillDown={handleDrillDown}
      />
    </>
  );
};
